'use client';

import { useState } from 'react';
import { formatDateShort } from '@/lib/date-format';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TimelineEvent {
  pathway: string;
  phase: string;
  phaseLocal: string;
  dateStart: string;
  dateEnd: string;
  href?: string;
}

interface AdmissionCalendarProps {
  events: TimelineEvent[];
  lang: string;
  isGraduate?: boolean;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Jan 1 2026 00:00 UTC */
const RANGE_START = new Date('2026-01-01T00:00:00');
/** Dec 31 2026 23:59 UTC — full 12 months */
const RANGE_END = new Date('2026-12-31T23:59:59');
const RANGE_MS = RANGE_END.getTime() - RANGE_START.getTime();

const MONTHS_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_ID = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agt', 'Sep', 'Okt', 'Nov', 'Des'];

/** Ordered list of known pathways for consistent row ordering */
const PATHWAY_ORDER = ['SNBP', 'UTBK-SNBT', 'Mandiri', 'SPAN-PTKIN', 'UM-PTKIN'];

/** Color scheme per pathway — uses design-system tokens for theme compatibility */
const PATHWAY_COLORS: Record<string, { bar: string; chip: string; chipActive: string; text: string }> = {
  'SNBP': {
    bar: 'bg-semantic-success/80',
    chip: 'border-semantic-success/30 text-semantic-success',
    chipActive: 'bg-semantic-success/12 border-semantic-success text-semantic-success',
    text: 'text-text-primary',
  },
  'UTBK-SNBT': {
    bar: 'bg-semantic-info/80',
    chip: 'border-semantic-info/30 text-semantic-info',
    chipActive: 'bg-semantic-info/12 border-semantic-info text-semantic-info',
    text: 'text-text-primary',
  },
  'Mandiri': {
    bar: 'bg-cat-purple/80',
    chip: 'border-cat-purple/30 text-cat-purple',
    chipActive: 'bg-cat-purple/12 border-cat-purple text-cat-purple',
    text: 'text-text-primary',
  },
  'SPAN-PTKIN': {
    bar: 'bg-cat-teal/80',
    chip: 'border-cat-teal/30 text-cat-teal',
    chipActive: 'bg-cat-teal/12 border-cat-teal text-cat-teal',
    text: 'text-text-primary',
  },
  'UM-PTKIN': {
    bar: 'bg-semantic-warning/80',
    chip: 'border-semantic-warning/30 text-semantic-warning',
    chipActive: 'bg-semantic-warning/12 border-semantic-warning text-semantic-warning',
    text: 'text-text-primary',
  },
};

const GRAD_COLORS: Array<{ bar: string; chip: string; chipActive: string; text: string }> = [
  { bar: 'bg-brand/80', chip: 'border-brand/30 text-brand', chipActive: 'bg-brand/12 border-brand text-brand', text: 'text-white' },
  { bar: 'bg-semantic-info/80', chip: 'border-semantic-info/30 text-semantic-info', chipActive: 'bg-semantic-info/12 border-semantic-info text-semantic-info', text: 'text-text-primary' },
  { bar: 'bg-cat-purple/80', chip: 'border-cat-purple/30 text-cat-purple', chipActive: 'bg-cat-purple/12 border-cat-purple text-cat-purple', text: 'text-white' },
  { bar: 'bg-semantic-success/80', chip: 'border-semantic-success/30 text-semantic-success', chipActive: 'bg-semantic-success/12 border-semantic-success text-semantic-success', text: 'text-text-primary' },
  { bar: 'bg-semantic-warning/80', chip: 'border-semantic-warning/30 text-semantic-warning', chipActive: 'bg-semantic-warning/12 border-semantic-warning text-semantic-warning', text: 'text-text-primary' },
];

const DEFAULT_COLORS = PATHWAY_COLORS['Mandiri'];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getColors(pathway: string, isGraduate?: boolean, index?: number) {
  if (isGraduate && index !== undefined) {
    return GRAD_COLORS[index % GRAD_COLORS.length];
  }
  return PATHWAY_COLORS[pathway] ?? DEFAULT_COLORS;
}

/** Convert a date string to a percentage position within the Jan-Dec range */
function dateToPercent(dateStr: string): number {
  const d = new Date(dateStr + 'T00:00:00');
  const ms = d.getTime() - RANGE_START.getTime();
  return Math.max(0, Math.min(100, (ms / RANGE_MS) * 100));
}

/** Group events by pathway, preserving a stable order */
function groupByPathway(events: TimelineEvent[]): Map<string, TimelineEvent[]> {
  const map = new Map<string, TimelineEvent[]>();

  for (const ev of events) {
    const existing = map.get(ev.pathway);
    if (existing) {
      existing.push(ev);
    } else {
      map.set(ev.pathway, [ev]);
    }
  }

  return map;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function FilterChips({
  pathways,
  active,
  onToggle,
  lang,
  isGraduate,
}: {
  pathways: string[];
  active: Set<string>;
  onToggle: (p: string) => void;
  lang: string;
  isGraduate?: boolean;
}) {
  const isId = lang === 'id';
  return (
    <div className="flex flex-wrap gap-2" role="group" aria-label={isId ? 'Filter jalur' : 'Filter pathways'}>
      {pathways.map((p, i) => {
        const colors = getColors(p, isGraduate, i);
        const isActive = active.has(p);
        return (
          <button
            key={p}
            type="button"
            onClick={() => onToggle(p)}
            aria-pressed={isActive}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
              isActive ? colors.chipActive : colors.chip
            }`}
          >
            {p}
          </button>
        );
      })}
    </div>
  );
}

/** Month header columns for the Gantt chart */
function MonthHeaders({ lang }: { lang: string }) {
  const months = lang === 'id' ? MONTHS_ID : MONTHS_EN;
  return (
    <div className="flex border-b border-border-subtle">
      {/* Row label spacer */}
      <div className="w-28 shrink-0 md:w-36" />
      {/* Month columns */}
      <div className="flex flex-1">
        {months.map((m, i) => (
          <div
            key={i}
            className="flex-1 border-l border-border-subtle px-1 py-1.5 text-center text-caption font-medium text-text-secondary uppercase"
          >
            {m}
          </div>
        ))}
      </div>
    </div>
  );
}

/** A single pathway row in the Gantt chart */
function GanttRow({ pathway, phases, isGraduate, index }: { pathway: string; phases: TimelineEvent[]; isGraduate?: boolean; index: number }) {
  const colors = getColors(pathway, isGraduate, index);

  return (
    <div className="group flex items-stretch border-b border-border-subtle last:border-b-0">
      {/* Pathway label */}
      <div className="flex w-28 shrink-0 items-center px-2 py-2 md:w-36 md:px-3">
        {phases[0]?.href ? (
          <a href={phases[0].href} className="text-xs font-semibold text-brand truncate hover:underline">
            {pathway}
          </a>
        ) : (
          <span className="text-xs font-semibold text-text-primary truncate">{pathway}</span>
        )}
      </div>

      {/* Bar area */}
      <div className="relative flex-1 py-2">
        {/* Vertical month gridlines */}
        {Array.from({ length: 12 }).map((_, i) => (
          <div
            key={i}
            className="absolute top-0 bottom-0 border-l border-border-subtle"
            style={{ left: `${(i / 12) * 100}%` }}
          />
        ))}

        {/* Phase bars */}
        {phases.map((ph, i) => {
          const left = dateToPercent(ph.dateStart);
          const right = dateToPercent(ph.dateEnd);
          const width = Math.max(right - left, 0.8); // min width so tiny ranges are visible

          const BarContent = (
            <div
              className={`absolute top-1/2 -translate-y-1/2 h-5 rounded ${colors.bar} flex items-center justify-center overflow-hidden transition-transform hover:scale-[1.02]`}
              style={{ left: `${left}%`, width: `${width}%` }}
              title={`${ph.phase}: ${ph.dateStart} - ${ph.dateEnd}`}
              role="img"
              aria-label={`${pathway} ${ph.phase}: ${ph.dateStart} to ${ph.dateEnd}`}
            >
              {width > 6 && (
                <span className={`truncate px-1 text-caption font-medium leading-none ${colors.text}`}>
                  {ph.phase}
                </span>
              )}
            </div>
          );

          return ph.href ? (
            <a key={`${ph.phase}-${i}`} href={ph.href}>
              {BarContent}
            </a>
          ) : (
            <div key={`${ph.phase}-${i}`}>
              {BarContent}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Mobile vertical timeline view */
function MobileTimeline({
  grouped,
  lang,
  isGraduate,
}: {
  grouped: Map<string, TimelineEvent[]>;
  lang: string;
  isGraduate?: boolean;
}) {
  return (
    <div className="space-y-4">
      {Array.from(grouped.entries()).map(([pathway, phases], idx) => {
        const colors = getColors(pathway, isGraduate, idx);
        return (
          <div key={pathway}>
            {phases[0]?.href ? (
              <a href={phases[0].href} className="text-sm font-bold text-brand hover:underline">
                {pathway}
              </a>
            ) : (
              <h4 className="text-sm font-bold text-text-primary">{pathway}</h4>
            )}
            <div className="mt-2 ml-2 border-l-2 border-border-subtle pl-4 space-y-3">
              {phases.map((ph, i) => (
                <div key={`${ph.phase}-${i}`} className="relative">
                  {/* Dot on the timeline line */}
                  <div
                    className={`absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full ${colors.bar} ring-2 ring-surface-card`}
                  />
                  <p className="text-sm font-medium text-text-primary">
                    {lang === 'id' ? ph.phaseLocal : ph.phase}
                  </p>
                  <p className="text-xs text-text-secondary">
                    {formatDateShort(ph.dateStart, lang)} – {formatDateShort(ph.dateEnd, lang)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function AdmissionCalendar({ events, lang, isGraduate }: AdmissionCalendarProps) {
  const isId = lang === 'id';
  const grouped = groupByPathway(events);
  const allPathways = Array.from(grouped.keys());

  const [activePathways, setActivePathways] = useState<Set<string>>(() => new Set(allPathways));

  function handleToggle(p: string) {
    setActivePathways(prev => {
      const next = new Set(prev);
      if (next.has(p)) {
        // Don't allow deselecting all
        if (next.size > 1) next.delete(p);
      } else {
        next.add(p);
      }
      return next;
    });
  }

  // Filter grouped data by active pathways
  const filtered = new Map<string, TimelineEvent[]>();
  for (const [pathway, phases] of grouped) {
    if (activePathways.has(pathway)) {
      filtered.set(pathway, phases);
    }
  }

  return (
    <div className="rounded-xl border border-border-subtle bg-surface-card p-4 md:p-6">
      {/* Header */}
      <h3 className="font-heading text-lg font-bold text-text-primary">
        {isId ? 'Kalender Penerimaan Mahasiswa 2026' : 'University Admission Calendar 2026'}
      </h3>
      <p className="mt-1 text-sm text-text-secondary">
        {isId
          ? `Timeline jalur masuk ${isGraduate ? 'pascasarjana' : 'PTN dan PTKIN'} tahun 2026.`
          : `2026 ${isGraduate ? 'graduate' : 'university'} admission timelines.`}
      </p>

      {/* Filter chips */}
      <div className="mt-4">
        <FilterChips
          pathways={allPathways}
          active={activePathways}
          onToggle={handleToggle}
          lang={lang}
          isGraduate={isGraduate}
        />
      </div>

      {/* Desktop: Gantt chart */}
      <div className="mt-4 hidden md:block overflow-x-auto">
        <div className="min-w-[800px]">
          <MonthHeaders lang={lang} />
          {Array.from(filtered.entries()).map(([pathway, phases], i) => (
            <GanttRow key={pathway} pathway={pathway} phases={phases} isGraduate={isGraduate} index={i} />
          ))}
        </div>
      </div>

      {/* Mobile: vertical timeline */}
      <div className="mt-4 md:hidden">
        <MobileTimeline grouped={filtered} lang={lang} isGraduate={isGraduate} />
      </div>

      {/* Legend */}
      <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1">
        {allPathways.map((p, i) => {
          const colors = getColors(p, isGraduate, i);
          return (
            <div key={p} className="flex items-center gap-1.5">
              <span className={`inline-block h-2.5 w-2.5 rounded-sm ${colors.bar}`} />
              <span className="text-caption text-text-secondary">{p}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
